import React from 'react';
import { CheckCircle, SkipForward, Clock } from 'lucide-react';
import { useVerificationWizard } from '../useVerificationWizard';
import { VERIFICATION_STEP_NAMES, VERIFICATION_TOTAL_STEPS } from '../../../types/verification';
import type { StepStatus } from '../../../types/verification';
import '../../../pages/VerificationWizardPage.css';

const VerifyReviewStep: React.FC = () => {
  const { state } = useVerificationWizard();

  const stepsToReview = VERIFICATION_TOTAL_STEPS - 1; // Exclude review step itself
  let confirmedCount = 0;
  let updatedCount = 0;
  let skippedCount = 0;
  let pendingCount = 0;

  for (let i = 0; i < stepsToReview; i++) {
    const status = state.stepStatuses[i]?.status || 'pending';
    if (status === 'confirmed') confirmedCount++;
    else if (status === 'updated') updatedCount++;
    else if (status === 'skipped') skippedCount++;
    else pendingCount++;
  }

  const getStatusIcon = (status: StepStatus) => {
    switch (status) {
      case 'confirmed': return <CheckCircle size={18} className="icon-confirmed" />;
      case 'updated': return <CheckCircle size={18} className="icon-updated" />;
      case 'skipped': return <SkipForward size={18} className="icon-skipped" />;
      default: return <Clock size={18} className="icon-pending" />;
    }
  };

  const getStatusText = (status: StepStatus) => {
    switch (status) {
      case 'confirmed': return 'Confirmed - No changes';
      case 'updated': return 'Updated & saved';
      case 'skipped': return 'Skipped';
      default: return 'Not reviewed';
    }
  };

  const allReviewed = pendingCount === 0;

  return (
    <div className="wizard-step">
      <h2>Verification Review</h2>
      <p className="step-description">
        Review the status of each section before completing the verification.
      </p>

      <div className="review-summary-row">
        <div className="summary-badge summary-badge-verified">
          {confirmedCount + updatedCount} verified
        </div>
        {skippedCount > 0 && (
          <div className="summary-badge summary-badge-skipped">
            {skippedCount} skipped
          </div>
        )}
        {pendingCount > 0 && (
          <div className="summary-badge summary-badge-pending">
            {pendingCount} not reviewed
          </div>
        )}
      </div>

      <div className="review-steps-list">
        {Array.from({ length: stepsToReview }).map((_, i) => {
          const status = state.stepStatuses[i]?.status || 'pending';
          const notes = state.stepStatuses[i]?.notes;

          return (
            <div
              key={VERIFICATION_STEP_NAMES[i]}
              className={`review-step-row${status === 'pending' ? ' review-step-row--pending' : ''}`}
            >
              {getStatusIcon(status)}
              <div className="review-step-row-info">
                <span className="review-step-name">
                  {VERIFICATION_STEP_NAMES[i]}
                </span>
                <span className={`review-step-status-text${status === 'pending' ? ' review-step-status-text--pending' : ''}`}>
                  {getStatusText(status)}
                </span>
                {notes && (
                  <div className="review-step-notes">
                    Note: {notes}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!allReviewed && (
        <div className="review-warning-banner">
          Some sections have not been reviewed. You can still complete the verification,
          but it's recommended to review all sections first.
        </div>
      )}

      <div className="review-info-banner">
        Click <strong>"Complete Verification"</strong> to stamp this clinic as verified.
        This will update the Last Verified Date and remove the clinic from the verification queue.
      </div>
    </div>
  );
};

export default VerifyReviewStep;
